import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import { BiCalendar } from "react-icons/bi";
import { XIcon } from "lucide-react";

const CustomDatePicker = ({
  onDateSelect,
  placeholder = "Select Date",
  value,
  onChange,
  innerClassName = "",
  float = true,
  isType = "daily",
  isAllday = false,
  className = "",
  disabled = false,
}) => {
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedDate, setSelectedDate] = useState(
    value ? moment(value) : null
  );

  const [viewMode, setViewMode] = useState(() => {
    if (isType === "yearly") return "year";
    if (isType === "monthly") return "month";
    return "date";
  });
  const [yearPageStart, setYearPageStart] = useState(moment().year() - 6);
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef(null);
  const months = moment.months();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (value) {
      setSelectedDate(moment(value));
      setCurrentMonth(moment(value));
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  useEffect(() => {
    if (isType === "yearly") {
      setViewMode("year");
    } else if (isType === "monthly") {
      setViewMode("month");
    } else {
      setViewMode("date");
    }
  }, [isType]);

  const handleDateClick = (day) => {
    if (!isAllday && day.isBefore(moment(), "day")) return;

    setSelectedDate(day);
    setIsOpen(false);
    onDateSelect?.(day.toDate());
    onChange?.(day.toDate());
  };

  const handleMonthSelect = (monthIndex) => {
    const newMonth = moment(currentMonth).month(monthIndex);
    setCurrentMonth(newMonth);
    setSelectedDate(newMonth.startOf("month"));

    if (isType === "monthly") {
      setIsOpen(false);
      onDateSelect?.(newMonth.startOf("month").toDate());
      onChange?.(newMonth.startOf("month").toDate());
    } else {
      setViewMode("date");
    }
  };

  const handleYearSelect = (year) => {
    const newMonth = moment(currentMonth).year(year);
    setCurrentMonth(newMonth);

    if (isType === "yearly") {
      setSelectedDate(newMonth.startOf("year"));
      setIsOpen(false);
      onDateSelect?.(newMonth.startOf("year").toDate());
      onChange?.(newMonth.startOf("year").toDate());
    } else if (isType === "monthly") {
      setViewMode("month");
    } else {
      setViewMode("date");
    }
  };

  const handleInputClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      if (!isOpen) {
        if (isType === "yearly") {
          setViewMode("year");
        } else if (isType === "monthly") {
          setViewMode("month");
        } else {
          setViewMode("date");
        }
      }
      setIsOpen(!isOpen);
    }
  };

  const handleNavClick = (direction, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (viewMode === "date") {
      const newMonth =
        direction === "prev"
          ? moment(currentMonth).subtract(1, "month")
          : moment(currentMonth).add(1, "month");

      if (!isAllday && newMonth.isBefore(moment(), "month")) return;
      setCurrentMonth(newMonth);
    } else if (viewMode === "month" && isType === "monthly") {
      const newMonth =
        direction === "prev"
          ? moment(currentMonth).subtract(1, "year")
          : moment(currentMonth).add(1, "year");

      if (!isAllday && newMonth.isBefore(moment(), "year")) return;
      setCurrentMonth(newMonth);
    } else if (viewMode === "year") {
      const newYearPageStart =
        yearPageStart + (direction === "prev" ? -12 : 12);
      if (!isAllday && newYearPageStart < moment().year()) return;
      setYearPageStart(newYearPageStart);
    }
  };

  const renderHeader = () => {
    if (isType === "yearly") {
      return (
        <div className="p-3 bg-gray-100 rounded-md">
          <div className="flex justify-between items-center mb-2">
            <button
              type="button"
              onClick={(e) => handleNavClick("prev", e)}
              className="text-gray-500 text-lg cursor-pointer hover:text-black"
            >
              &lt;
            </button>
            <span className="font-medium">{currentMonth.format("YYYY")}</span>
            <button
              type="button"
              onClick={(e) => handleNavClick("next", e)}
              className="text-gray-500 text-lg cursor-pointer hover:text-black"
            >
              &gt;
            </button>
          </div>
        </div>
      );
    }

    if (isType === "monthly") {
      return (
        <div className="p-3 bg-gray-100 rounded-md">
          <div className="flex justify-between items-center mb-2">
            <button
              type="button"
              onClick={(e) => handleNavClick("prev", e)}
              className="text-gray-500 text-lg cursor-pointer hover:text-black"
            >
              &lt;
            </button>
            <span
              className="font-medium cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setViewMode("year");
              }}
            >
              {currentMonth.format("YYYY")}
            </span>
            <button
              type="button"
              onClick={(e) => handleNavClick("next", e)}
              className="text-gray-500 text-lg cursor-pointer hover:text-black"
            >
              &gt;
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="p-3 bg-gray-100 rounded-md">
        <div className="flex justify-between items-center mb-2">
          <button
            type="button"
            onClick={(e) => handleNavClick("prev", e)}
            className="text-gray-500 text-lg cursor-pointer hover:text-black"
          >
            &lt;
          </button>

          <span className="font-medium cursor-pointer space-x-1">
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setViewMode("month");
              }}
            >
              {currentMonth.format("MMMM")}
            </span>{" "}
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setViewMode("year");
              }}
            >
              {currentMonth.format("YYYY")}
            </span>
          </span>

          <button
            type="button"
            onClick={(e) => handleNavClick("next", e)}
            className="text-gray-500 text-lg cursor-pointer hover:text-black"
          >
            &gt;
          </button>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = [];
    const startDate = moment(currentMonth).startOf("week");
    for (let i = 0; i < 7; i++) {
      days.push(
        <div
          key={i}
          className="text-center text-sm py-2 font-medium text-gray-600"
        >
          {moment(startDate).add(i, "days").format("ddd")}
        </div>
      );
    }
    return <div className="grid grid-cols-7">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = moment(currentMonth).startOf("month");
    const monthEnd = moment(currentMonth).endOf("month");
    const startDate = moment(monthStart).startOf("week");
    const endDate = moment(monthEnd).endOf("week");
    const today = moment().startOf("day");

    const rows = [];
    let days = [];
    let day = moment(startDate);

    while (day.isSameOrBefore(endDate, "day")) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = moment(day);
        const isPastDate = !isAllday && cloneDay.isBefore(today, "day");
        const isCurrentMonth = cloneDay.isSame(monthStart, "month");
        const isSelected =
          selectedDate && cloneDay.isSame(selectedDate, "day");

        days.push(
          <div
            key={cloneDay.format("DD-MM-YYYY")}
            className={`w-8 ms-2 h-8 flex justify-center items-center text-center text-xs rounded-full transition-all
                        ${!isCurrentMonth
                ? "text-gray-400"
                : isPastDate
                  ? "text-gray-300 cursor-not-allowed"
                  : "cursor-pointer"
              }
                        ${isSelected
                ? "bg-[#E03334] text-white"
                : !isPastDate && isCurrentMonth
                  ? "hover:bg-[#E03334]/10"
                  : ""
              }
                        `}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isPastDate) handleDateClick(cloneDay);
            }}
          >
            {cloneDay.format("D")}
          </div>
        );
        day.add(1, "day");
      }
      rows.push(
        <div className="grid grid-cols-7 my-1" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    return <div>{rows}</div>;
  };

  const renderMonthSelection = () => {
    const currentDate = moment();
    const currentYear = currentDate.year();
    const currentMonthIndex = currentDate.month();
    const selectedYear = currentMonth.year();

    return (
      <div className="grid grid-cols-3 gap-2 p-4">
        {months.map((month, idx) => {
          const isPastMonth = !isAllday && (
            selectedYear < currentYear ||
            (selectedYear === currentYear && idx < currentMonthIndex)
          );

          return (
            <div
              key={month}
              className={`text-center w-full h-10 flex justify-center items-center text-sm rounded-md ${isPastMonth
                ? "text-gray-300 cursor-not-allowed"
                : "hover:bg-[#E03334]/10 cursor-pointer"
                }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isPastMonth) {
                  handleMonthSelect(idx);
                }
              }}
            >
              {month}
            </div>
          );
        })}
      </div>
    );
  };

  const renderYearSelection = () => {
    const yearItems = [];
    const currentYear = moment().year();

    for (let i = 0; i < 12; i++) {
      const year = yearPageStart + i;
      const isPastYear = !isAllday && year < currentYear;

      yearItems.push(
        <div
          key={year}
          className={`text-center w-full h-10 flex justify-center items-center text-sm rounded-md ${isPastYear
            ? "text-gray-300 cursor-not-allowed"
            : "hover:bg-[#E03334]/10 cursor-pointer"
            }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isPastYear) {
              handleYearSelect(year);
            }
          }}
        >
          {year}
        </div>
      );
    }

    return <div className="grid grid-cols-3 gap-2 p-4">{yearItems}</div>;
  };

  const handleClearClick = () => {
    setSelectedDate(null);
    onDateSelect?.(null);
    onChange?.(null);
  };

  const getFormattedValue = () => {
    if (!selectedDate) return "";

    if (isType === "yearly") {
      return selectedDate.format("YYYY");
    } else if (isType === "monthly") {
      return selectedDate.format("MMMM YYYY");
    } else {
      return selectedDate.format("DD/MM/YYYY");
    }
  };

  return (
    <div className={`relative ${className}`} ref={datePickerRef}>
      <input
        type="text"
        value={getFormattedValue()}
        placeholder={placeholder}
        onClick={handleInputClick}
        readOnly
        disabled={disabled}
        className={`w-full px-3 text-sm py-3 ${innerClassName} rounded-md cursor-pointer focus:outline-none ${disabled ? "cursor-not-allowed" : ""
          }`}
      />
      {selectedDate && (
        <XIcon onClick={handleClearClick} className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer w-5 h-5" />
      )}
      <BiCalendar onClick={handleInputClick} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer text-xl" />

      {isOpen && (
        <div className={`absolute ${float ? "top-12 left-0" : "bottom-12 left-0"} z-50 md:w-[300px] w-full p-2 border bg-white border-gray-300 rounded-md shadow-lg`}>
          {renderHeader()}
          {(isType === "daily" || isType === "weekly") && (
            <>
              {viewMode === "date" && renderDays()}
              {viewMode === "date" && renderCells()}
              {viewMode === "month" && renderMonthSelection()}
              {viewMode === "year" && renderYearSelection()}
            </>
          )}
          {isType === "monthly" && (
            <>
              {viewMode === "month" && renderMonthSelection()}
              {viewMode === "year" && renderYearSelection()}
            </>
          )}
          {isType === "yearly" && (
            <>
              {viewMode === "year" && renderYearSelection()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
